import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Route as RouteIcon, Sparkles } from "lucide-react";
import { theme, card, btnPrimary, inputStyle, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { LoadingBlock } from "../components/Spinner";
import { SheetCard, DimensionHeading, StatusTag, type StatusTone } from "../components/Drawing";
import { PortalLink } from "../components/PortalLink";
import { fmtDateOnly } from "../lib/dateOnly";
import { fmtDateTime } from "../lib/format";
import { runAiJob } from "../lib/aiJob";
import { secondaryBtn } from "./resilience/shared";

// Gap remediation roadmap (docs/IMPROVEMENT-PROGRAMME.md "Gap remediation roadmap", backend lib/remediationRoadmap.ts).
// A standard's open Statement of Applicability gaps, sequenced by rule (the management system clauses first, then the
// controls, then internal audit and management review) and dated inside the window before the certification audit.
// The AI may estimate each step's effort and suggest an owner; that is advice. A member accepts a step into a
// corrective action, with the owner and due date they choose. Writes need controls:write on the server.

type Phase = "foundations" | "controls" | "check";
interface Step {
  controlId: string; label: string; phase: Phase; status: string; closers: Array<{ label: string; href: string }>;
  dependsOn: string[]; effortDays: number; effortSource: "ai" | "rule"; suggestedOwnerUserId: string | null; reason: string | null;
  dueDate: string; action: { id: string; displayId: number | null; status: string; roadmap: boolean } | null;
}
interface Roadmap {
  frameworkId: string; label: string; target: { date: string; source: "booked" | "target" } | null; windowEnd: string;
  steps: Step[]; totalEffortDays: number; workingDays: number; tight: boolean; overdue: boolean; estimatedAt: string | null;
}
interface Member { id: string; name: string; role: string }

const PHASES: Array<[Phase, string]> = [["foundations", "Foundations: the management system clauses"], ["controls", "Controls"], ["check", "Check: internal audit and management review"]];
const STATUS_TONE: Record<string, StatusTone> = { GAP: "gap", NOT_STARTED: "none", PARTIAL: "partial", IN_PROGRESS: "partial" };
const STATUS_LABEL: Record<string, string> = { GAP: "Gap", NOT_STARTED: "Not started", PARTIAL: "Partly met", IN_PROGRESS: "In progress" };
const caRef = (n: number | null) => (n ? `CA-${String(n).padStart(3, "0")}` : "An action");

export function RemediationRoadmapPage() {
  const [params, setParams] = useSearchParams();
  const [standards, setStandards] = useState<Array<{ frameworkId: string; label: string }> | null>(null);
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [estimating, setEstimating] = useState(false);
  const fw = params.get("standard") ?? standards?.[0]?.frameworkId ?? null;

  useEffect(() => {
    api.get<{ standards: Array<{ frameworkId: string; label: string }> }>("/remediation-roadmap")
      .then((r) => setStandards(r.standards)).catch((e) => setError(apiErrorMessage(e, "Could not load your standards.")));
  }, []);
  const load = useCallback(async () => {
    if (!fw) return;
    try {
      const r = await api.get<{ roadmap: Roadmap; members: Member[] }>(`/remediation-roadmap/${fw}`);
      setRoadmap(r.roadmap); setMembers(r.members); setError(null);
    } catch (e) { setError(apiErrorMessage(e, "Could not build the roadmap.")); }
  }, [fw]);
  useEffect(() => { setRoadmap(null); void load(); }, [load]);

  async function estimate() {
    if (!fw) return;
    setEstimating(true); setError(null);
    try {
      await runAiJob(`/remediation-roadmap/${fw}/estimate`, (id) => `/remediation-roadmap/estimate-jobs/${id}`);
      await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not estimate the roadmap.")); }
    finally { setEstimating(false); }
  }

  const names = new Map(members.map((m) => [m.id, m.name]));
  return (
    <div className="page-pad">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}><RouteIcon size={20} /> Gap roadmap</h1>
        <p style={{ ...pageSubtitle, maxWidth: 780 }}>
          Your open gaps on a standard, in the order to close them and dated to finish before the certification audit. The management system clauses come first, the controls after the risk assessment that selects them, and internal audit and management review last. Accept a step to make it a corrective action with an owner and a due date.
        </p>
      </div>
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 12 }}>{error}</div>}
      {!standards ? <LoadingBlock /> : standards.length === 0 ? (
        <SheetCard><div style={text.hint}>None of your standards has requirements to plan.</div></SheetCard>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
            <select aria-label="Standard" value={fw ?? ""} onChange={(e) => setParams({ standard: e.target.value })} style={{ ...inputStyle, width: 260 }}>
              {standards.map((s) => <option key={s.frameworkId} value={s.frameworkId}>{s.label}</option>)}
            </select>
            <button type="button" disabled={estimating || !roadmap?.steps.some((s) => !s.action)} style={{ ...btnPrimary, display: "inline-flex", alignItems: "center", gap: 6 }} onClick={() => void estimate()}>
              <Sparkles size={14} /> {estimating ? "Estimating…" : "Estimate effort and owners"}
            </button>
            {roadmap?.estimatedAt && <span style={text.meta}>Estimated {fmtDateTime(roadmap.estimatedAt)}; advice only</span>}
          </div>
          {!roadmap ? <LoadingBlock /> : <RoadmapView roadmap={roadmap} members={members} names={names} onChanged={setRoadmap} />}
        </>
      )}
    </div>
  );
}

function RoadmapView({ roadmap, members, names, onChanged }: { roadmap: Roadmap; members: Member[]; names: Map<string, string>; onChanged: (r: Roadmap) => void }) {
  return (
    <>
      <SheetCard style={{ marginBottom: 14 }}>
        <div style={{ ...text.body }}>
          {roadmap.target
            ? <>Working towards the {roadmap.target.source === "booked" ? "audit your certification body booked" : "target you set"} on <strong>{fmtDateOnly(roadmap.target.date)}</strong>. Controls finish by {fmtDateOnly(roadmap.windowEnd)}, leaving time for internal audit and management review.</>
            : <>No audit date is booked or set, so the plan runs to {fmtDateOnly(roadmap.windowEnd)}. Set a target on the <PortalLink to="/schedule" style={{ color: theme.color.accentText }}>calendar's certification countdown</PortalLink> to plan against it.</>}
        </div>
        <div style={{ ...text.hint, marginTop: 4 }}>
          {roadmap.steps.length} open step{roadmap.steps.length === 1 ? "" : "s"}, about {roadmap.totalEffortDays} person-days against {roadmap.workingDays} working days.
          {roadmap.overdue && " The audit is too close for a window: everything is due now."}
          {roadmap.tight && !roadmap.overdue && " That is more than one person's working days: share the steps out, or move the target yourself."}
        </div>
      </SheetCard>
      {roadmap.steps.length === 0 && <SheetCard><div style={text.hint}>No open gaps on {roadmap.label}. Nothing to plan.</div></SheetCard>}
      {PHASES.map(([phase, title]) => {
        const steps = roadmap.steps.filter((s) => s.phase === phase);
        if (!steps.length) return null;
        return (
          <SheetCard key={phase} style={{ marginBottom: 14 }}>
            <DimensionHeading count={steps.length}>{title}</DimensionHeading>
            {steps.map((s, i) => <StepRow key={s.controlId} step={s} first={i === 0} framework={roadmap.frameworkId} members={members} names={names} onChanged={onChanged} />)}
          </SheetCard>
        );
      })}
    </>
  );
}

function StepRow({ step, first, framework, members, names, onChanged }: { step: Step; first: boolean; framework: string; members: Member[]; names: Map<string, string>; onChanged: (r: Roadmap) => void }) {
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState(step.suggestedOwnerUserId ?? "");
  const [due, setDue] = useState(step.dueDate);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ roadmap: Roadmap }>(`/remediation-roadmap/${framework}/steps/${encodeURIComponent(step.controlId)}/accept`, { ownerUserId: owner || null, dueDate: due });
      onChanged(r.roadmap); setOpen(false);
    } catch (e) { setError(apiErrorMessage(e, "Could not accept the step.")); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ padding: "10px 0", borderTop: first ? "none" : `1px solid ${theme.color.border}` }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ ...text.body, fontWeight: 600, color: theme.color.heading }}>{step.label}</div>
          <div style={{ ...text.hint }}>
            Due {fmtDateOnly(step.dueDate)} · about {step.effortDays} person-day{step.effortDays === 1 ? "" : "s"} ({step.effortSource === "ai" ? "AI estimate" : "rule of thumb"})
            {step.suggestedOwnerUserId && names.get(step.suggestedOwnerUserId) ? ` · suggested owner ${names.get(step.suggestedOwnerUserId)}` : ""}
          </div>
          {step.reason && <div style={text.meta}>{step.reason}</div>}
          {step.dependsOn.length > 0 && step.phase !== "check" && <div style={text.meta}>After the risk assessment and treatment clauses.</div>}
          {step.closers.length > 0 && (
            <div style={{ ...text.meta, marginTop: 2 }}>Closed by records in: {step.closers.map((c, i) => <span key={c.href}>{i ? ", " : ""}<PortalLink to={c.href} style={{ color: theme.color.accentText }}>{c.label}</PortalLink></span>)}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <StatusTag tone={STATUS_TONE[step.status] ?? "none"}>{STATUS_LABEL[step.status] ?? step.status}</StatusTag>
          {step.action
            ? <PortalLink to={`/improvement?action=${step.action.id}`} style={{ ...text.hint, color: theme.color.accentText }}>{caRef(step.action.displayId)} covers it</PortalLink>
            : !open && <button type="button" style={{ ...btnPrimary, padding: "3px 10px", fontSize: 12 }} onClick={() => setOpen(true)}>Accept</button>}
        </div>
      </div>
      {open && (
        <div style={{ ...card, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <select aria-label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} style={{ ...inputStyle, width: 240 }}>
            <option value="">No owner yet</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
          </select>
          <input type="date" aria-label="Due date" value={due} onChange={(e) => setDue(e.target.value)} style={{ ...inputStyle, width: 170 }} />
          <button type="button" disabled={busy || !due} style={{ ...btnPrimary, padding: "3px 10px", fontSize: 12 }} onClick={() => void accept()}>Make it an action</button>
          <button type="button" style={secondaryBtn} onClick={() => setOpen(false)}>Cancel</button>
        </div>
      )}
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}
